import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import {
  PaintBrush,
  CircleNotch,
  Sparkle,
  CheckCircle,
  WarningCircle,
  DownloadSimple,
  Trash,
  InstagramLogo,
  FacebookLogo,
  YoutubeLogo,
  TwitterLogo,
  ArrowsClockwise,
  Lightbulb,
  UploadSimple,
  PencilSimple,
  X as XIcon,
  ImageSquare,
} from "@phosphor-icons/react";

const PRESET_ICONS = {
  "ig-square": InstagramLogo,
  "ig-portrait": InstagramLogo,
  "ig-story": InstagramLogo,
  "fb-ad": FacebookLogo,
  "yt-thumb": YoutubeLogo,
  "twitter-banner": TwitterLogo,
};

const BATCH_COUNTS = [1, 2, 3, 4, 6, 8];

export default function DesignStudio() {
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState("ig-square");
  const [headline, setHeadline] = useState("");
  const [subline, setSubline] = useState("");
  const [prompt, setPrompt] = useState("");
  const [batchCount, setBatchCount] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Brand-aware suggestions pulled from /design/headlines + /design/style-presets
  const [headlineSuggestions, setHeadlineSuggestions] = useState([]);
  const [headlinesLoading, setHeadlinesLoading] = useState(false);
  const [stylePresets, setStylePresets] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState(null);

  // User-uploaded reference photos (image-to-image source)
  const [references, setReferences] = useState([]);
  const [selectedReferenceId, setSelectedReferenceId] = useState(null);
  const [uploadingRef, setUploadingRef] = useState(false);

  const fetchReferences = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/design/references`);
      setReferences(res.data.references || []);
    } catch (_) { /* silent */ }
  }, []);

  const uploadReference = async (file) => {
    if (!file) return;
    setUploadingRef(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/design/references/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReferences((prev) => [res.data, ...prev]);
      setSelectedReferenceId(res.data.id);
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingRef(false);
    }
  };

  const deleteReference = async (refId) => {
    if (!window.confirm("Remove this reference photo?")) return;
    try {
      await axios.delete(`${API}/design/references/${refId}`);
      setReferences((prev) => prev.filter((r) => r.id !== refId));
      if (selectedReferenceId === refId) setSelectedReferenceId(null);
    } catch (_) { /* silent */ }
  };

  const fetchPresets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/design/presets`);
      setPresets(res.data.presets || []);
    } catch (_) { /* silent */ }
  }, []);

  const fetchHeadlines = useCallback(async (refresh = false) => {
    setHeadlinesLoading(true);
    try {
      const res = await axios.get(`${API}/design/headlines`, { params: { count: 12, refresh: refresh ? 1 : 0 } });
      setHeadlineSuggestions(res.data.headlines || []);
    } catch (_) { /* silent */ }
    finally { setHeadlinesLoading(false); }
  }, []);

  const fetchStylePresets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/design/style-presets`);
      setStylePresets(res.data.styles || []);
    } catch (_) { /* silent */ }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/design/jobs`);
      setJobs(res.data.jobs || []);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPresets(); fetchHeadlines(); fetchStylePresets(); fetchJobs(); fetchReferences(); }, [fetchPresets, fetchHeadlines, fetchStylePresets, fetchJobs, fetchReferences]);

  // Live-poll every 4s while any job is rendering
  useEffect(() => {
    const anyActive = jobs.some(j => ["starting", "processing"].includes(j.status));
    if (!anyActive) return;
    const t = setInterval(fetchJobs, 4000);
    return () => clearInterval(t);
  }, [jobs, fetchJobs]);

  const handleGenerate = async () => {
    if (!prompt.trim() && !headline.trim()) {
      setError("Add a prompt or headline first");
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      await axios.post(`${API}/design/generate`, {
        preset_id: selectedPreset,
        prompt: prompt.trim(),
        headline: headline.trim(),
        subline: subline.trim(),
        title: headline.trim() || prompt.trim().slice(0, 80),
        count: batchCount,
        reference_id: selectedReferenceId || undefined,
      });
      await fetchJobs();
    } catch (e) {
      setError(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const applyStyle = (style) => {
    setSelectedStyle(style.id);
    setPrompt(style.prompt);
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm("Delete this design?")) return;
    try {
      await axios.delete(`${API}/design/jobs/${jobId}`);
      fetchJobs();
    } catch (_) { /* silent */ }
  };

  return (
    <div className="space-y-5" data-testid="design-studio-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#E91E8C]/10 flex items-center justify-center">
          <PaintBrush size={20} weight="duotone" className="text-[#E91E8C]" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-zinc-900">Design Studio</h2>
          <p className="text-zinc-500 text-sm">Branded graphics + ad banners powered by Nano Banana — auto-styled with your Brand KB.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* LEFT: Format picker + form */}
        <div className="card p-5 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">1. Pick a format</label>
            <div className="grid grid-cols-3 gap-2" data-testid="design-presets-grid">
              {presets.map((p) => {
                const Icon = PRESET_ICONS[p.id] || PaintBrush;
                const isSelected = selectedPreset === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPreset(p.id)}
                    className={`text-left p-3 rounded-xl border transition-all ${
                      isSelected ? "border-[#E91E8C] bg-[#E91E8C]/5 ring-2 ring-[#E91E8C]/20" : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
                    }`}
                    data-testid={`design-preset-${p.id}`}
                  >
                    <Icon size={16} weight="duotone" className={isSelected ? "text-[#E91E8C]" : "text-zinc-400"} />
                    <p className={`text-xs font-semibold mt-1.5 ${isSelected ? "text-[#E91E8C]" : "text-zinc-800"}`}>{p.label}</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">{p.dimensions}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">2. Headline <span className="font-normal text-zinc-300 normal-case">(big text)</span></label>
              <button
                onClick={() => fetchHeadlines(true)}
                disabled={headlinesLoading}
                className="flex items-center gap-1 text-[11px] text-[#E91E8C] hover:text-[#d4177d] disabled:opacity-50"
                data-testid="design-refresh-headlines"
              >
                <ArrowsClockwise size={11} className={headlinesLoading ? "animate-spin" : ""} weight="bold" />
                Refresh ideas
              </button>
            </div>
            <input
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder='e.g. "Stop scrolling. Read this."'
              className="input-field text-sm"
              maxLength={80}
              data-testid="design-headline"
            />
            {/* Brand-KB headline suggestion chips */}
            {headlineSuggestions.length > 0 && (
              <div className="mt-2 flex items-start gap-1.5">
                <Lightbulb size={13} weight="duotone" className="text-amber-500 flex-shrink-0 mt-1" />
                <div className="flex flex-wrap gap-1.5" data-testid="design-headline-chips">
                  {headlineSuggestions.map((h, i) => (
                    <button
                      key={i}
                      onClick={() => setHeadline(h)}
                      className={`text-[11px] px-2 py-1 rounded-full border transition-all ${
                        headline === h ? "bg-[#E91E8C] text-white border-[#E91E8C]" : "bg-zinc-50 hover:bg-[#E91E8C]/10 border-zinc-200 text-zinc-700 hover:border-[#E91E8C]/40"
                      }`}
                      data-testid={`design-headline-chip-${i}`}
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">3. Subline <span className="font-normal text-zinc-300 normal-case">(optional · smaller text)</span></label>
            <input
              value={subline}
              onChange={(e) => setSubline(e.target.value)}
              placeholder='e.g. "How I went from $0 to 6 figures"'
              className="input-field text-sm"
              maxLength={120}
              data-testid="design-subline"
            />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">4. Style prompt <span className="font-normal text-zinc-300 normal-case">(describe the visual)</span></label>
            {/* Style preset pills */}
            {stylePresets.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="design-style-chips">
                {stylePresets.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => applyStyle(s)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-all ${
                      selectedStyle === s.id ? "bg-[#E91E8C] text-white border-[#E91E8C]" : "bg-zinc-50 hover:bg-[#E91E8C]/10 border-zinc-200 text-zinc-700 hover:border-[#E91E8C]/40"
                    }`}
                    data-testid={`design-style-chip-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
            <textarea
              value={prompt}
              onChange={(e) => { setPrompt(e.target.value); setSelectedStyle(null); }}
              placeholder='e.g. "minimalist pink + black gradient with glowing accents, lifestyle aesthetic, soft glow"'
              className="input-field text-sm min-h-[80px] resize-none"
              maxLength={400}
              data-testid="design-prompt"
            />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">5. Reference photo <span className="font-normal text-zinc-300 normal-case">(optional · use your own image as the base)</span></label>
            <div className="flex flex-wrap items-start gap-2" data-testid="design-references">
              <label
                className={`group relative flex flex-col items-center justify-center w-20 h-20 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                  uploadingRef ? "border-zinc-200 bg-zinc-50 cursor-wait" : "border-zinc-300 hover:border-[#E91E8C] hover:bg-[#E91E8C]/5"
                }`}
                data-testid="design-reference-upload"
              >
                {uploadingRef ? (
                  <CircleNotch size={18} className="animate-spin text-zinc-400" />
                ) : (
                  <>
                    <UploadSimple size={18} className="text-zinc-400 group-hover:text-[#E91E8C]" />
                    <span className="text-[10px] text-zinc-400 group-hover:text-[#E91E8C] mt-1 font-medium">Upload</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  className="hidden"
                  disabled={uploadingRef}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadReference(f);
                    e.target.value = ""; // allow re-upload of same file
                  }}
                />
              </label>
              {references.map((ref) => {
                const isSelected = selectedReferenceId === ref.id;
                return (
                  <div key={ref.id} className="relative w-20 h-20">
                    <button
                      onClick={() => setSelectedReferenceId(isSelected ? null : ref.id)}
                      className={`w-full h-full rounded-xl overflow-hidden border-2 transition-all ${
                        isSelected ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30" : "border-zinc-200 hover:border-zinc-400"
                      }`}
                      data-testid={`design-reference-${ref.id}`}
                      title={ref.original_name || "Reference"}
                    >
                      <img src={ref.public_url} alt="" className="w-full h-full object-cover" />
                      {isSelected && (
                        <div className="absolute inset-0 bg-[#E91E8C]/15 flex items-center justify-center">
                          <CheckCircle size={20} weight="fill" className="text-[#E91E8C]" />
                        </div>
                      )}
                    </button>
                    <button
                      onClick={() => deleteReference(ref.id)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-zinc-200 hover:bg-red-50 hover:border-red-300 flex items-center justify-center text-zinc-400 hover:text-red-500 shadow-sm"
                      data-testid={`design-reference-delete-${ref.id}`}
                    >
                      <XIcon size={10} weight="bold" />
                    </button>
                  </div>
                );
              })}
            </div>
            {selectedReferenceId ? (
              <p className="text-[11px] text-[#E91E8C] mt-2 flex items-center gap-1">
                <ImageSquare size={12} weight="duotone" />
                Nano Banana will use this photo as the base and re-style it with your prompt.
              </p>
            ) : references.length === 0 ? (
              <p className="text-[11px] text-zinc-400 mt-2">Tip: upload a product shot or moodboard and we'll re-style it into a branded graphic.</p>
            ) : (
              <p className="text-[11px] text-zinc-400 mt-2">Tap a thumbnail to use that photo as the base, or skip to generate from prompt only.</p>
            )}
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">6. How many designs?</label>
            <div className="flex gap-1.5 flex-wrap" data-testid="design-batch-options">
              {BATCH_COUNTS.map((n) => (
                <button
                  key={n}
                  onClick={() => setBatchCount(n)}
                  className={`px-3 py-1.5 text-xs rounded-lg border font-semibold transition-all ${
                    batchCount === n ? "bg-[#E91E8C] text-white border-[#E91E8C]" : "bg-white hover:bg-zinc-50 border-zinc-200 text-zinc-700"
                  }`}
                  data-testid={`design-batch-${n}`}
                >
                  {n} {n === 1 ? "design" : "variations"}
                </button>
              ))}
            </div>
            {batchCount > 1 && (
              <p className="text-[10px] text-zinc-400 mt-1.5">
                Each variation generates independently from the same prompt — Nano Banana isn't fully deterministic so you'll see real diversity. ~${(batchCount * 0.02).toFixed(2)} total · {batchCount * 25}-{batchCount * 45}s if rendered in parallel.
              </p>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
              <WarningCircle size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={generating || (!prompt.trim() && !headline.trim())}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#E91E8C] text-white rounded-xl font-bold text-sm hover:bg-[#d4177d] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="design-generate-btn"
          >
            {generating ? <><CircleNotch size={16} className="animate-spin" /> Queuing…</> : <><Sparkle size={16} weight="fill" /> Generate {batchCount > 1 ? `${batchCount} designs` : "design"}</>}
          </button>

          <p className="text-[11px] text-zinc-400 leading-relaxed">
            Auto-styled with your Brand KB colors & voice. Saved to Content Vault when complete.
          </p>
        </div>

        {/* RIGHT: Recent jobs */}
        <div className="space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500">Recent designs</h3>
          {loading ? (
            <div className="flex items-center justify-center py-10"><CircleNotch size={20} className="animate-spin text-zinc-300" /></div>
          ) : jobs.length === 0 ? (
            <div className="card p-6 text-center text-sm text-zinc-400" data-testid="design-no-jobs">
              No designs yet — make your first one ←
            </div>
          ) : (
            <div className="space-y-3" data-testid="design-jobs-list">
              {jobs.map(j => <DesignJobCard key={j.id} job={j} onDelete={() => deleteJob(j.id)} onEditSubmitted={fetchJobs} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DesignJobCard({ job, onDelete, onEditSubmitted }) {
  const isProcessing = ["starting", "processing"].includes(job.status);
  const isDone = job.status === "completed";
  const isFailed = job.status === "failed";

  const [showEdit, setShowEdit] = useState(false);
  const [editPrompt, setEditPrompt] = useState("");
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState(null);

  const submitEdit = async () => {
    if (!editPrompt.trim()) return;
    setEditing(true);
    setEditError(null);
    try {
      await axios.post(`${API}/design/jobs/${job.id}/edit`, {
        prompt: editPrompt.trim(),
      });
      setShowEdit(false);
      setEditPrompt("");
      if (onEditSubmitted) onEditSubmitted();
    } catch (e) {
      setEditError(e.response?.data?.detail || "Edit failed");
    } finally {
      setEditing(false);
    }
  };

  return (
    <div className="card p-4 space-y-3" data-testid={`design-job-${job.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-900 truncate">{job.title || job.headline || "Untitled design"}</p>
          <p className="text-[11px] text-zinc-400 mt-0.5">{job.preset_label} · {job.dimensions}</p>
        </div>
        <button onClick={onDelete} className="text-zinc-300 hover:text-red-500" data-testid={`design-delete-${job.id}`}>
          <Trash size={14} />
        </button>
      </div>

      {isProcessing && (
        <div className="flex items-center gap-2 bg-zinc-50 rounded-lg p-2 text-xs text-zinc-600">
          <CircleNotch size={14} className="animate-spin" />
          {job.current_step === "submitting" ? "Sending to Nano Banana..." :
           job.current_step === "rendering" ? "Rendering design (typically 15-40s)..." :
           "Queued..."}
        </div>
      )}

      {isFailed && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
          <WarningCircle size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
          <span>{job.error || "Generation failed"}</span>
        </div>
      )}

      {isDone && job.image_url && (
        <>
          <a href={job.image_url} target="_blank" rel="noopener noreferrer" className="block">
            <img
              src={job.image_url}
              alt={job.title}
              loading="lazy"
              className="w-full rounded-lg border border-zinc-200 hover:opacity-90 transition-opacity"
              data-testid={`design-image-${job.id}`}
            />
          </a>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1 text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
              <CheckCircle size={11} weight="fill" /> Saved to Vault
            </span>
            <a
              href={job.image_url}
              download={`design-${job.preset_id}-${job.id.slice(0, 8)}.png`}
              className="flex items-center gap-1 text-[11px] bg-zinc-50 hover:bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-full font-medium"
              data-testid={`design-download-${job.id}`}
            >
              <DownloadSimple size={11} /> Download
            </a>
            <button
              onClick={() => setShowEdit((v) => !v)}
              className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium transition-colors ${
                showEdit ? "bg-[#E91E8C] text-white" : "bg-zinc-50 hover:bg-[#E91E8C]/10 text-zinc-700 hover:text-[#E91E8C]"
              }`}
              data-testid={`design-edit-toggle-${job.id}`}
            >
              <PencilSimple size={11} weight="bold" /> {showEdit ? "Cancel" : "Edit / refine"}
            </button>
          </div>
          {showEdit && (
            <div className="border-t border-zinc-100 pt-3 space-y-2" data-testid={`design-edit-form-${job.id}`}>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Describe the change</label>
              <textarea
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                placeholder='e.g. "Make the background darker and add a sun glare in the top right"'
                className="input-field text-xs min-h-[60px] resize-none"
                maxLength={300}
                data-testid={`design-edit-prompt-${job.id}`}
              />
              {editError && (
                <p className="text-[11px] text-red-600">{editError}</p>
              )}
              <button
                onClick={submitEdit}
                disabled={editing || !editPrompt.trim()}
                className="w-full bg-[#E91E8C] hover:bg-[#d4177d] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                data-testid={`design-edit-submit-${job.id}`}
              >
                {editing ? (<><CircleNotch size={12} className="animate-spin" /> Submitting...</>) : (<><Sparkle size={12} weight="fill" /> Re-render with this change</>)}
              </button>
              <p className="text-[10px] text-zinc-400">A new design will appear above in ~30s. Original stays saved.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
